/**
 * #801 — the PURE half of the protected-branch partition's client reading (no React, no
 * i18n instance): how a preview reads, which `files` i18n key a refusal maps to, and the one
 * result sentence per op. `protectedBranchFlow.tsx` re-exports all of it for the UI; the
 * agent's `move_file` tool (worker side) renders the same sentences in English, so the
 * drive's toast and the tool's answer say the same thing.
 */
import { rootAccessBlocksSplit, type PartitionPreview } from '@papan/types'

export type PartitionOp = 'delete' | 'move'
export type T = (key: string, opts?: Record<string, unknown>) => string

/** Anything protected in the preview — listed or only counted. */
export const previewHasProtected = (p: PartitionPreview): boolean =>
  p.protected.length > 0 || p.hiddenProtectedCount > 0

/** How many branches a split left behind — the listed ones plus the hidden count. */
export const skippedCountOf = (p: PartitionPreview): number => p.protected.length + p.hiddenProtectedCount

/**
 * What a preview means for the act:
 *  - `plain`       nothing protected — the surface's existing flow, unchanged
 *  - `entityRoot`  an app/project root: refused whole (E1), info only
 *  - `needsManage` a move whose root has its own access the mover can't manage
 *                  (`rootAccessBlocksSplit`): info only
 *  - `nothing`     something protected and nothing removable: info only
 *  - `split`       confirm with the preview, then act with `expectRemovable`
 */
export type PartitionVerdict = 'plain' | 'blocked' | 'entityRoot' | 'needsManage' | 'nothing' | 'split'
export function classifyPartitionPreview(p: PartitionPreview, op: PartitionOp): PartitionVerdict {
  // L4 round 6 (M-17c): the server ran the move's own judgements for the destination and
  // says it WILL be refused — shown as a refusal, whatever else the preview holds.
  if (op === 'move' && p.blocked) return 'blocked'
  if (!previewHasProtected(p)) return 'plain'
  if (p.entityRoot) return 'entityRoot'
  if (op === 'move' && rootAccessBlocksSplit(p.rootAccess)) return 'needsManage'
  if (p.removable.length === 0) return 'nothing'
  return 'split'
}

/**
 * The `files` i18n key for a #801 refusal, or `null` when `err` isn't one. Reads
 * the drive's shape (`code: 'PROTECTED_BRANCHES'` + flags, from `request()` or
 * `MoveRefusedError`) and the share view's (`error: 'protected_branches'` + flags)
 * alike. A bare PROTECTED_BRANCHES (no flag) is "nothing here can be … by you".
 */
export function partitionRefusalKey(err: unknown, op: PartitionOp): string | null {
  if (!err || typeof err !== 'object') return null
  const x = err as { code?: unknown; error?: unknown; entityRoot?: unknown; needsManage?: unknown; wouldLoseAccess?: unknown }
  const code = (typeof x.code === 'string' ? x.code : typeof x.error === 'string' ? x.error : '').toUpperCase()
  if (code === 'PARTITION_CHANGED') return 'protectedBranches.changedRetry'
  if (code === 'SUBTREE_TOO_DEEP') return 'protectedBranches.tooDeep'
  // The share view's 403 for a split whose shell access copy the grant judge refused: its
  // `code` is the JUDGE's own, so the router's `error` marker is what names it.
  if (x.error === 'shell_governance') return 'protectedBranches.shellGovernance'
  if (code !== 'PROTECTED_BRANCHES') return null
  if (x.entityRoot === true) return 'protectedBranches.entityRootRefused'
  if (x.wouldLoseAccess === true) return 'protectedBranches.wouldLoseAccess'
  if (x.needsManage === true) return 'protectedBranches.needsManage'
  return op === 'move' ? 'protectedBranches.nothingMovable' : 'protectedBranches.nothingRemovable'
}

/** The part of a delete response the result sentence reads. */
export interface DeleteResultShape { binId?: string; binIds?: string[]; skipped?: PartitionPreview }

/**
 * The sentence for a delete's outcome, or `null` for an ordinary whole delete
 * (the surface keeps its own "Deleted X"). A split names what was skipped; a
 * delete that binned nothing (a replay, or everything became protected) says so.
 */
export function deleteResultMessage(t: T, r: DeleteResultShape): string | null {
  const binned = r.binIds?.length ?? (r.binId ? 1 : 0)
  if (r.skipped) {
    if (binned === 0) return t('protectedBranches.nothingLeft')
    return t('protectedBranches.resultDeleted', { count: binned, skipped: skippedCountOf(r.skipped) })
  }
  if (!r.binId && r.binIds && r.binIds.length === 0) return t('protectedBranches.nothingLeft')
  return null
}

/** The part of a move response the result sentence reads. */
export interface MoveResultShape {
  split?: {
    replay?: boolean; moved?: string[]; skipped?: PartitionPreview
    linksStayed?: number; orgSharesStayed?: number; appGrantsStayed?: number
  }
  /** #982: a folder moved across organisations kept this many parts where it was (the mover can't remove them —
   *  the folder stays there as a shell around them). */
  movedKept?: number
}

/**
 * The sentence for a move's outcome, or `null` for an ordinary whole move. A
 * split names what stayed behind (and the root's links / org shares / app
 * access that stayed with it); a replay moved nothing.
 */
export function moveResultMessage(t: T, r: MoveResultShape): string | null {
  if (r.movedKept) return t('protectedBranches.movedKept', { count: r.movedKept })
  const s = r.split
  if (!s) return null
  if (s.replay || (s.moved?.length ?? 0) === 0) return t('protectedBranches.nothingLeftMove')
  const parts = [t('protectedBranches.resultMoved', { count: s.moved!.length, skipped: s.skipped ? skippedCountOf(s.skipped) : 0 })]
  if (s.linksStayed) parts.push(t('protectedBranches.linksStay', { count: s.linksStayed }))
  if (s.orgSharesStayed) parts.push(t('protectedBranches.orgSharesStay', { count: s.orgSharesStayed }))
  if (s.appGrantsStayed) parts.push(t('protectedBranches.appAccessStays', { count: s.appGrantsStayed }))
  return parts.join(' ')
}

